import { Material } from '@ess/jg-rule-executor/dist/src/domain/material';
import { GetAllMaterials_allMaterials_edges_node } from '@src/app/generated/GetAllMaterials';
import { Id } from '@ess/jg-rule-executor';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import { MaterialInput } from '@src/app/generated/globalTypes';
import { Plane, Vector2 } from 'three';
import { PortalVector2 } from '@src/app/model/portalVector2';
import { PortalPlane } from '@src/app/model/portalPlane';

export class PortalModuleMaterial extends Material implements ToJsonInputTranslatable {
  constructor(
    id: Id<Material>,
    name: string,
    color?: string,
    roughness?: number,
    metalness?: number,
    map?: string,
    lightMap?: string,
    lightMapIntensity?: number,
    aoMap?: string,
    aoMapIntensity?: number,
    emissive?: string,
    emissiveIntensity?: number,
    emissiveMap?: string,
    bumpMap?: string,
    bumpScale?: number,
    normalMap?: string,
    normalMapType?: number,
    normalScale?: Vector2,
    displacementMap?: string,
    displacementScale?: number,
    displacementBias?: number,
    roughnessMap?: string,
    metalnessMap?: string,
    alphaMap?: string,
    envMap?: string,
    envMapIntensity?: number,
    wireframe?: boolean,
    wireframeLinewidth?: number,
    fog?: boolean,
    flatShading?: boolean,
    alphaTest?: number,
    alphaToCoverage?: boolean,
    blendDst?: number,
    blendDstAlpha?: number,
    blendEquation?: number,
    blendEquationAlpha?: number,
    blending?: number,
    blendSrc?: number,
    blendSrcAlpha?: number,
    clipIntersection?: boolean,
    clippingPlanes?: Plane[],
    clipShadows?: boolean,
    colorWrite?: boolean,
    depthFunc?: number,
    depthTest?: boolean,
    depthWrite?: boolean,
    opacity?: number,
    polygonOffset?: boolean,
    polygonOffsetFactor?: number,
    polygonOffsetUnits?: number,
    precision?: string,
    premultipliedAlpha?: boolean,
    dithering?: boolean,
    side?: number,
    shadowSide?: number,
    toneMapped?: boolean,
    transparent?: boolean,
    vertexColors?: boolean,
    visible?: boolean,
    format?: number,
    stencilWrite?: boolean,
    stencilFunc?: number,
    stencilRef?: number,
    stencilWriteMask?: number,
    stencilFuncMask?: number,
    stencilFail?: number,
    stencilZFail?: number,
    stencilZPass?: number
  ) {
    super(
      id,
      name,
      color,
      roughness,
      metalness,
      map,
      lightMap,
      lightMapIntensity,
      aoMap,
      aoMapIntensity,
      emissive,
      emissiveIntensity,
      emissiveMap,
      bumpMap,
      bumpScale,
      normalMap,
      normalMapType,
      normalScale,
      displacementMap,
      displacementScale,
      displacementBias,
      roughnessMap,
      metalnessMap,
      alphaMap,
      envMap,
      envMapIntensity,
      wireframe,
      wireframeLinewidth,
      fog,
      flatShading,
      alphaTest,
      alphaToCoverage,
      blendDst,
      blendDstAlpha,
      blendEquation,
      blendEquationAlpha,
      blending,
      blendSrc,
      blendSrcAlpha,
      clipIntersection,
      clippingPlanes,
      clipShadows,
      colorWrite,
      depthFunc,
      depthTest,
      depthWrite,
      opacity,
      polygonOffset,
      polygonOffsetFactor,
      polygonOffsetUnits,
      precision,
      premultipliedAlpha,
      dithering,
      side,
      shadowSide,
      toneMapped,
      transparent,
      vertexColors,
      visible,
      format,
      stencilWrite,
      stencilFunc,
      stencilRef,
      stencilWriteMask,
      stencilFuncMask,
      stencilFail,
      stencilZFail,
      stencilZPass
    );
  }

  static fromGql(gql: GetAllMaterials_allMaterials_edges_node): PortalModuleMaterial {
    return new PortalModuleMaterial(
      gql.id,
      gql.name,
      gql.color,
      gql.roughness,
      gql.metalness,
      gql.map,
      gql.lightMap,
      gql.lightMapIntensity,
      gql.aoMap,
      gql.aoMapIntensity,
      gql.emissive,
      gql.emissiveIntensity,
      gql.emissiveMap,
      gql.bumpMap,
      gql.bumpScale,
      gql.normalMap,
      gql.normalMapType,
      gql.normalScale ? PortalVector2.fromGql(gql.normalScale) : undefined,
      gql.displacementMap,
      gql.displacementScale,
      gql.displacementBias,
      gql.roughnessMap,
      gql.metalnessMap,
      gql.alphaMap,
      gql.envMap,
      gql.envMapIntensity,
      gql.wireframe,
      gql.wireframeLinewidth,
      gql.fog,
      gql.flatShading,
      gql.alphaTest,
      gql.alphaToCoverage,
      gql.blendDst,
      gql.blendDstAlpha,
      gql.blendEquation,
      gql.blendEquationAlpha,
      gql.blending,
      gql.blendSrc,
      gql.blendSrcAlpha,
      gql.clipIntersection,
      gql.clippingPlanes ? gql.clippingPlanes.map(p => PortalPlane.fromGql(p)) : undefined,
      gql.clipShadows,
      gql.colorWrite,
      gql.depthFunc,
      gql.depthTest,
      gql.depthWrite,
      gql.opacity,
      gql.polygonOffset,
      gql.polygonOffsetFactor,
      gql.polygonOffsetUnits,
      gql.precision,
      gql.premultipliedAlpha,
      gql.dithering,
      gql.side,
      gql.shadowSide,
      gql.toneMapped,
      gql.transparent,
      gql.vertexColors,
      gql.visible,
      gql.format,
      gql.stencilWrite,
      gql.stencilFunc,
      gql.stencilRef,
      gql.stencilWriteMask,
      gql.stencilFuncMask,
      gql.stencilFail,
      gql.stencilZFail,
      gql.stencilZPass
    );
  }

  toGqlInput(): MaterialInput {
    return {
      name: this.name,
      color: this.color,
      roughness: this.roughness,
      metalness: this.metalness,
      map: this.map,
      lightMap: this.lightMap,
      lightMapIntensity: this.lightMapIntensity,
      aoMap: this.aoMap,
      aoMapIntensity: this.aoMapIntensity,
      emissive: this.emissive,
      emissiveIntensity: this.emissiveIntensity,
      emissiveMap: this.emissiveMap,
      bumpMap: this.bumpMap,
      bumpScale: this.bumpScale,
      normalMap: this.normalMap,
      normalMapType: this.normalMapType,
      normalScale: this.normalScale,
      displacementMap: this.displacementMap,
      displacementScale: this.displacementScale,
      displacementBias: this.displacementBias,
      roughnessMap: this.roughnessMap,
      metalnessMap: this.metalnessMap,
      alphaMap: this.alphaMap,
      envMap: this.envMap,
      envMapIntensity: this.envMapIntensity,
      wireframe: this.wireframe,
      wireframeLinewidth: this.wireframeLinewidth,
      fog: this.fog,
      flatShading: this.flatShading,
      alphaTest: this.alphaTest,
      alphaToCoverage: this.alphaToCoverage,
      blendDst: this.blendDst,
      blendDstAlpha: this.blendDstAlpha,
      blendEquation: this.blendEquation,
      blendEquationAlpha: this.blendEquationAlpha,
      blending: this.blending,
      blendSrc: this.blendSrc,
      blendSrcAlpha: this.blendSrcAlpha,
      clipIntersection: this.clipIntersection,
      clippingPlanes: this.clippingPlanes,
      clipShadows: this.clipShadows,
      colorWrite: this.colorWrite,
      depthFunc: this.depthFunc,
      depthTest: this.depthTest,
      depthWrite: this.depthWrite,
      opacity: this.opacity,
      polygonOffset: this.polygonOffset,
      polygonOffsetFactor: this.polygonOffsetFactor,
      polygonOffsetUnits: this.polygonOffsetUnits,
      precision: this.precision,
      premultipliedAlpha: this.premultipliedAlpha,
      dithering: this.dithering,
      side: this.side,
      shadowSide: this.shadowSide,
      toneMapped: this.toneMapped,
      transparent: this.transparent,
      vertexColors: this.vertexColors,
      visible: this.visible,
      format: this.format,
      stencilWrite: this.stencilWrite,
      stencilFunc: this.stencilFunc,
      stencilRef: this.stencilRef,
      stencilWriteMask: this.stencilWriteMask,
      stencilFuncMask: this.stencilFuncMask,
      stencilFail: this.stencilFail,
      stencilZFail: this.stencilZFail,
      stencilZPass: this.stencilZPass,
    };
  }

  static toJsonInput(materialInput: MaterialInput): JSON {
    return { material: materialInput } as object as JSON;
  }
}
